import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, EventEmitter, Input, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { ApiHelper } from "../../../helpers/apihelper";
import { FulfilmentRequest, StartLoadMessage } from "../../../helpers/self-service/models/types";
import {
  IpBgpSubscriptionDetails,
  IpSubscriptionDetails,
  L2vpnSubscriptionDetails,
  LightPathBothSubscriptionDetails,
  isIp,
  isIpBgp,
  isL2vpn,
  isLightPath,
} from "../../../pages/subscription-detail/types";
import { AnalyticsService } from "../../../services/analytics.service";
import { AnalyticsAction } from "../../../services/analytics.types";
import { AuthService } from "../../../services/authservice";
import { SelfserviceAddPort } from "../../selfservice/selfservice-add-port/selfservice-add-port";
import { SelfserviceBfd } from "../../selfservice/selfservice-bfd/selfservice-bfd";
import { SelfserviceBgp } from "../../selfservice/selfservice-bgp/selfservice-bgp";
import { SelfserviceDdosAutoMitigation } from "../../selfservice/selfservice-ddos-auto-mitigation/selfservice-ddos-auto-mitigation";
import { SelfserviceDdos } from "../../selfservice/selfservice-ddos/selfservice-ddos";
import { SelfserviceGracefulRestart } from "../../selfservice/selfservice-graceful-restart/selfservice-graceful-restart";
import { SelfserviceIPPrefixes } from "../../selfservice/selfservice-ip-prefixes/selfservice-ip-prefixes";
import { SelfserviceLoader } from "../../selfservice/selfservice-loader/selfservice-loader";
import { SelfserviceRemovePort } from "../../selfservice/selfservice-remove-port/selfservice-remove-port";
import { SelfserviceRps } from "../../selfservice/selfservice-rps/selfservice-rps";
import { SelfserviceSpeedPolicer } from "../../selfservice/selfservice-speedpolicer/selfservice-speedpolicer";
import { SelfserviceSuccess } from "../../selfservice/selfservice-success/selfservice-success";
import { SelfserviceUnavailable } from "../../selfservice/selfservice-unavailable/selfservice-unavailable";
import { SelfserviceVlanL2vpn } from "../../selfservice/selfservice-vlan-l2vpn/selfservice-vlan-l2vpn";
import { SelfserviceVlanLightpath } from "../../selfservice/selfservice-vlan-lightpath/selfservice-vlan-lightpath";
import { SelfServiceSubscription } from "./dialog-selfservice.types";

// TODO: this whole class needs to die (painfully). It's just a huge switch that dispatches to the different
// self-service actions.

export type SelfserviceStatusMessage = string;

const TITLE_KEYS: Record<string, string> = {
  "add-port": "Selfservice.Title.add-port",
  bfd: "Selfservice.Title.bfd",
  "graceful-restart": "Selfservice.Title.graceful-restart",
  bgp: "Selfservice.Title.bgp",
  ddos: "Selfservice.Title.ddos",
  "ip-prefixes": "Selfservice.Title.ip-prefixes",
  "remove-port": "Selfservice.Title.remove-port",
  rps: "Selfservice.Title.rps",
  sp: "Selfservice.Title.sp",
  "vlan-l2vpn": "Selfservice.Title.vlan-l2vpn",
  "vlan-lightpath": "Selfservice.Title.vlan-lightpath",
  autoMitigation: "Selfservice.Title.autoMitigation",
};

@Component({
  selector: "dialog-selfservice",
  templateUrl: "dialog-selfservice.html",
  styleUrls: ["dialog-selfservice.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    SelfserviceAddPort,
    SelfserviceBfd,
    SelfserviceBgp,
    SelfserviceGracefulRestart,
    SelfserviceDdos,
    SelfserviceDdosAutoMitigation,
    SelfserviceIPPrefixes,
    SelfserviceLoader,
    SelfserviceRemovePort,
    SelfserviceRps,
    SelfserviceSpeedPolicer,
    SelfserviceSuccess,
    SelfserviceUnavailable,
    SelfserviceVlanL2vpn,
    SelfserviceVlanLightpath,
    TranslatePipe,
  ],
})
export class DialogSelfServiceComponent<S> {
  dialogRef = inject<MatDialogRef<DialogSelfServiceComponent<S>>>(MatDialogRef);

  private translate = inject(TranslateService);
  private api = inject(ApiHelper);
  protected auth = inject(AuthService);
  protected data = inject(MAT_DIALOG_DATA);
  private analytics = inject(AnalyticsService);

  @Input() loader = false;
  @Input() computedTitle = "";
  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  @Input() processId: string;

  public sapIndex = signal<number>(this.data.sapIndex);
  public subscription = signal<SelfServiceSubscription>(this.data.subscription);
  protected description = computed(() => this.subscription().description);
  protected customerId = computed(() => this.subscription().customerId);
  protected insync = computed(() => this.subscription().insync);
  protected subscriptionId = computed(() => this.subscription().subscriptionId);
  protected title = computed(() => {
    const type: string = this.data.type;
    const key = TITLE_KEYS[type];
    // Fall back to the raw type so a missing mapping stays visible in the UI.
    return key ? this.translate.instant(key) : type;
  });
  protected selfServiceStatus = signal(true);

  public communication: EventEmitter<StartLoadMessage> = new EventEmitter();
  public loadCommunication: EventEmitter<SelfserviceStatusMessage> = new EventEmitter();
  public instance: any;
  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  public runningAction: string;
  protected success = false;
  // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
  selectedPrefix: string;

  isIp = isIp;

  constructor() {
    this.analytics.trackCurrentFeature(AnalyticsAction.SelfService, this.data.type);

    const dialogRef = this.dialogRef;

    dialogRef.disableClose = true;
    this.instance = this.data.instance;

    this.communication.subscribe((event) => {
      this.processId = event.processId;
      this.runningAction = event.action || "unknown";
      this.loader = true;
    });

    this.api.selfserviceStatusLatest((state) => {
      this.selfServiceStatus.set(state);
    });

    this.loadCommunication.subscribe((status) => {
      switch (status) {
        case "failed":
          alert("err");
          break;
        case "notify_support":
          this.notifySupport();
          break;
        case "error":
          this.notifySupport();
          break;
        case "completed":
          this.loader = false;
          this.success = true;
          break;
      }
    });
  }

  get titleSuffix() {
    return this.data.titleSuffix ? this.data.titleSuffix : null;
  }

  get ipSubscriptionDetails(): IpSubscriptionDetails {
    const s = this.subscription();
    if (isIp(s)) {
      return s;
    } else {
      throw Error("Attempting to use subscription as IP subscription while it isn't one");
    }
  }

  get ipBgpSubscriptionDetails(): IpBgpSubscriptionDetails {
    const s = this.subscription();
    if (isIpBgp(s)) {
      return s;
    } else {
      throw Error("Attempting to use subscription as IP BGP subscription while it isn't one");
    }
  }

  get l2vpnSubscriptionDetails(): L2vpnSubscriptionDetails {
    const s = this.subscription();
    if (isL2vpn(s)) {
      return s;
    } else {
      throw Error("Attempting to use subscription as L2VPN subscription while it isn't one");
    }
  }

  get lightpathSubscriptionDetails(): LightPathBothSubscriptionDetails {
    const s = this.subscription();
    if (isLightPath(s)) {
      return s;
    } else {
      throw Error("Attempting to use subscription as lightpath subscription while it isn't one");
    }
  }

  get selfServiceSpCompatible(): L2vpnSubscriptionDetails | LightPathBothSubscriptionDetails {
    const s = this.subscription();
    if (isL2vpn(s) || isLightPath(s)) {
      return s;
    } else {
      throw Error("Attempting to use subscription as L2VPN or LP/LR subscription while it isn't one");
    }
  }

  closeDialog(msg?: string): void {
    this.dialogRef.close(msg);
  }

  notifySupport(): void {
    const customerId = this.auth.viewingCustomer().customerId;
    // note: not the type from /components/models.
    // we don't need any verification / errorchecking here.
    const request: FulfilmentRequest = {
      contacts: [
        {
          fullName: this.auth.currentUser().displayName,
          email: this.auth.currentUser().email,
          phone: "06123", // placeholder, since we don't know the value.
        },
      ],
      serviceRequest: "problem",
      text: this.translate.instant("Selfservice.FulfilmentProblem.Message", {
        subscription_name: this.description(),
        action: this.runningAction,
        process_id: this.processId,
      }),
      subscriptionId: this.subscriptionId(),
      product: this.subscription().product.type,
    };

    this.api
      .add_new_fulfilment_problem(customerId, JSON.stringify(request))
      .then(() => {
        this.closeDialog("refresh");
      })
      .catch(() => this.closeDialog("refresh"));
  }
}
